import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebaseConfig';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useError } from '../../contexts/ErrorContext';
import OrderFilters from './OrderFilters';
import OrderCard from './OrderCard';
import OrderModals from './OrderModals';
import OrderNavigation from '../OrderNavigation';
import TrialBanner from '../TrialBanner';
import { useOrders } from './hooks/useOrders';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { getPlatformClass } from '../../utils/platformUtils';
import PlatformLayout from '../PlatformLayout';
import { useTranslation } from '../../hooks/useTranslation';
import PlatformText from '../PlatformText';
import ErrorBoundary from '../ErrorBoundary';
// TEMPORARILY DISABLED: BillPageModal import is temporarily disabled
// import BillPageModal from './BillPageModal';
import OrderValueApprovalModal from './OrderValueApprovalModal';
import LoadingScreen from '../LoadingScreen';
import './OrderManagement.css';

function OrderManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user] = useAuthState(auth);
  const { userRole, organizationId, organizationData, isLoading: orgLoading } = useOrganization();
  const { t } = useTranslation('orders');
  const { showErrorToast } = useError();
  const [currentTab, setCurrentTab] = useState('PLACED');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [personFilter, setPersonFilter] = useState('');
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [billModalUrl, setBillModalUrl] = useState(null);
  const [challanModalUrl, setChallanModalUrl] = useState(null);
  const [biltyModalUrl, setBiltyModalUrl] = useState(null);
  const [productModalUrl, setProductModalUrl] = useState(null);
  const [orderDetailsModalUrl, setOrderDetailsModalUrl] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [highlightOrderId, setHighlightOrderId] = useState(null);

  const { 
    orders, 
    isLoading: ordersLoading, 
    error: ordersError,
    handleApprove,
    handleCancel,
    handleDispatch,
    handleReadyToDispatch,
    handleEditSubmit,
    creatorNames,
    availableTabs,
    handleUploadBill,
    handleReplaceBill,
    handleUploadChallan,
    handleReplaceChallan,
    handleUploadBilty,
    handleReplaceBilty,
    viewChallan,
    viewBill,
    viewBilty,
    deleteOrderDetailsImage,
    replaceOrderDetailsImage,
    handleUploadProduct,
    handleReplaceProduct,
    // billPageModalProps, // TEMPORARILY DISABLED: Bill page modal is temporarily disabled
    openApprovalModal,
    approvalModalProps
  } = useOrders(user, currentTab, searchTerm, startDate, endDate, personFilter);

  // Set initial tab based on user role
  useEffect(() => {
    if (userRole === 'InventoryManager') {
      setCurrentTab('APPROVED');
    }
  }, [userRole]);

  // Handle deep linking from notifications via URL query parameters
  useEffect(() => {
    const orderIdParam = searchParams.get('orderId');
    if (orderIdParam) {
      setHighlightOrderId(orderIdParam);
      // Remove the query parameter from URL after reading it
      // This prevents it from persisting in browser history
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('orderId');
        return newParams;
      }, { replace: true });
      
      // Optional: Show a toast that order was highlighted
      // showErrorToast(`Showing order: ${orderIdParam}`, {
      //   variant: 'info',
      //   autoHide: true,
      //   duration: 3000
      // });
    }
  }, [searchParams, setSearchParams]);

  useSessionTimeout(user, navigate);

  // Show loading state if either organization or orders are loading
  if (orgLoading || ordersLoading) {
    return <LoadingScreen message={t('messages.loadingOrders')} variant="fullscreen" />;
  }

  // Show error state if there's an error
  if (ordersError) {
    return (
      <PlatformLayout>
      <div className="error-container">
        <PlatformText variant="heading" size="large" translationKey="errors.loadingOrders" namespace="orders" />
        <PlatformText variant="body" size="medium">{ordersError}</PlatformText>
        <button onClick={() => window.location.reload()}>
          <PlatformText translationKey="retry" namespace="common" />
        </button>
      </div>
      </PlatformLayout>
    );
  }

  // Show message if no organization
  if (!organizationId) {
    return (
      <PlatformLayout>
      <div className="error-container">
        <PlatformText variant="heading" size="large" translationKey="errors.noOrganization" namespace="orders" />
        <PlatformText variant="body" size="medium" translationKey="errors.noOrganizationMessage" namespace="orders" />
        <button onClick={() => navigate('/')}>
          <PlatformText translationKey="goToHome" namespace="common" />
        </button>
      </div>
      </PlatformLayout>
    );
  }

  const closeEditModal = () => {
    setEditingOrder(null);
  };

  // Add wrapper function to handle edit state
  const handleEdit = (order) => {
    setEditFormData({
      partyName: order.partyName,
      orderDetails: order.orderDetails,
      additionalNotes: order.additionalNotes || ''
    });
    setEditingOrder(order);
  };

  const handleLocalEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocalEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await handleEditSubmit(editingOrder.id, editFormData);
      setEditingOrder(null); // Close the modal after successful submission
    } catch (error) {
      showErrorToast(t('errors.updateOrderFailed'), {
        variant: 'error'
      });
    }
  };

  const renderOrderSection = (titleKey, orders) => {
    if (!orders || !Array.isArray(orders) || orders.length === 0) return null;
    
    return (
      <div className="order-section">
        <PlatformText variant="h3" className="section-title">{t(titleKey)}</PlatformText>
        <div className="orders-grid">
          {orders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              userRole={userRole}
              currentTab={currentTab}
              creatorNames={creatorNames}
              user={user}
              onEdit={handleEdit}
              onApprove={handleApprove}
              onCancel={handleCancel}
              onUploadBill={handleUploadBill}
              onReplaceBill={handleReplaceBill}
              onDispatch={handleDispatch}
              onReadyToDispatch={handleReadyToDispatch}
              onUploadBilty={handleUploadBilty}
              onReplaceBilty={handleReplaceBilty}
              onUploadChallan={handleUploadChallan}
              onReplaceChallan={handleReplaceChallan}
              onUploadProduct={handleUploadProduct}
              onReplaceProduct={handleReplaceProduct}
              setBillModalUrl={setBillModalUrl}
              setBiltyModalUrl={setBiltyModalUrl}
              setChallanModalUrl={setChallanModalUrl}
              setProductModalUrl={setProductModalUrl}
              setOrderDetailsModalUrl={setOrderDetailsModalUrl}
              setCurrentOrderId={setCurrentOrderId}
              viewChallan={viewChallan}
              viewBill={viewBill}
              viewBilty={viewBilty}
              organizationData={organizationData}
              onOpenApprovalModal={openApprovalModal}
                highlightOrderId={highlightOrderId}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary componentName="OrderManagement">
      <PlatformLayout>
      <div className={`container ${getPlatformClass()}`}>
        <TrialBanner />
        
        <OrderNavigation
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          availableTabs={availableTabs}
          userRole={userRole}
          isActiveScreen="orders"
        />

        <ErrorBoundary componentName="OrderFilters">
          <OrderFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            currentTab={currentTab}
            personFilter={personFilter}
            setPersonFilter={setPersonFilter}
            creatorNames={creatorNames}
          />
        </ErrorBoundary>

      {currentTab === 'DISPATCHED' ? (
        <>
          {!orders || (
           (orders.today?.length || 0) === 0 &&
           (orders.yesterday?.length || 0) === 0 &&
           (orders.older?.length || 0) === 0) ? (
            <div className="no-orders-message">
              {t('messages.noDispatchedOrders')}
            </div>
          ) : (
            <>
              {orders.today && orders.today.length > 0 && renderOrderSection('sections.today', orders.today)}
              {orders.yesterday && orders.yesterday.length > 0 && renderOrderSection('sections.yesterday', orders.yesterday)}
              {orders.older && orders.older.length > 0 && renderOrderSection('sections.olderOrders', orders.older)}
            </>
          )}
        </>
      ) : (
        <div className="orders-grid">
          {!Array.isArray(orders) || orders.length === 0 ? (
            <div className="no-orders-message">
              {currentTab === 'PLACED' && t('messages.noPlacedOrders')}
              {currentTab === 'APPROVED' && t('messages.noApprovedOrders')}
              {currentTab === 'READY_TO_DISPATCH' && t('messages.noReadyToDispatchOrders')}
            </div>
          ) : (
            orders.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                userRole={userRole}
                currentTab={currentTab}
                creatorNames={creatorNames}
                user={user}
                onEdit={handleEdit}
                onApprove={handleApprove}
                onCancel={handleCancel}
                onUploadBill={handleUploadBill}
                onReplaceBill={handleReplaceBill}
                onDispatch={handleDispatch}
                onReadyToDispatch={handleReadyToDispatch}
                onUploadBilty={handleUploadBilty}
                onReplaceBilty={handleReplaceBilty}
                onUploadChallan={handleUploadChallan}
                onReplaceChallan={handleReplaceChallan}
                onUploadProduct={handleUploadProduct}
                onReplaceProduct={handleReplaceProduct}
                setBillModalUrl={setBillModalUrl}
                setBiltyModalUrl={setBiltyModalUrl}
                setChallanModalUrl={setChallanModalUrl}
                setProductModalUrl={setProductModalUrl}
                setOrderDetailsModalUrl={setOrderDetailsModalUrl}
                setCurrentOrderId={setCurrentOrderId}
                viewChallan={viewChallan}
                viewBill={viewBill}
                viewBilty={viewBilty}
                organizationData={organizationData}
                onOpenApprovalModal={openApprovalModal}
                highlightOrderId={highlightOrderId}
              />
            ))
          )}
        </div>
      )}

      <OrderModals
        editingOrder={editingOrder}
        setEditingOrder={setEditingOrder}
        closeEditModal={closeEditModal}
        handleEditSubmit={handleLocalEditSubmit}
        handleEditChange={handleLocalEditChange}
        editFormData={editFormData}
        billModalUrl={billModalUrl}
        setBillModalUrl={setBillModalUrl}
        biltyModalUrl={biltyModalUrl}
        setBiltyModalUrl={setBiltyModalUrl}
        challanModalUrl={challanModalUrl}
        setChallanModalUrl={setChallanModalUrl}
        productModalUrl={productModalUrl}
        setProductModalUrl={setProductModalUrl}
        orderDetailsModalUrl={orderDetailsModalUrl}
        setOrderDetailsModalUrl={setOrderDetailsModalUrl}
        currentOrderId={currentOrderId}
        setCurrentOrderId={setCurrentOrderId}
        currentTab={currentTab}
        userRole={userRole}
        handleReplaceBill={handleReplaceBill}
        handleReplaceBilty={handleReplaceBilty}
        handleReplaceChallan={handleReplaceChallan}
        handleUploadBill={handleUploadBill}
        handleUploadChallan={handleUploadChallan}
        handleUploadBilty={handleUploadBilty}
        deleteOrderDetailsImage={deleteOrderDetailsImage}
        replaceOrderDetailsImage={replaceOrderDetailsImage}
        handleReplaceProduct={handleReplaceProduct}
        handleUploadProduct={handleUploadProduct}
        organizationData={organizationData}
      />
    </div>
    {/* TEMPORARILY DISABLED: BillPageModal is temporarily disabled to skip page number prompt */}
    {/* <BillPageModal
      {...billPageModalProps}
      onConfirm={() => billPageModalProps.onConfirm(handleUploadFile)}
    /> */}
    <OrderValueApprovalModal
      {...approvalModalProps}
    />
    </PlatformLayout>
    </ErrorBoundary>
  );
}

export default OrderManagement; 